import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Date formatting utilities
export function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
}

export function formatTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleTimeString('tr-TR', {
    hour: '2-digit',
    minute: '2-digit'
  });
}

export function formatDateTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('tr-TR', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export function getRelativeDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  
  const diffTime = date.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Bugün';
  if (diffDays === 1) return 'Yarın';
  if (diffDays === -1) return 'Dün';
  if (diffDays > 1 && diffDays <= 7) return `${diffDays} gün sonra`;
  if (diffDays < -1 && diffDays >= -7) return `${Math.abs(diffDays)} gün önce`;
  
  return formatDate(dateString);
}

// Priority utilities
export const priorityConfig = {
  urgent: { label: 'Acil', color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30' },
  high: { label: 'Yüksek', color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/30' },
  medium: { label: 'Orta', color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30' },
  low: { label: 'Düşük', color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30' }
};

export const statusConfig = {
  todo: { label: 'Yapılacak', color: 'text-zinc-400', bg: 'bg-zinc-500/10' },
  doing: { label: 'Devam Ediyor', color: 'text-blue-400', bg: 'bg-blue-500/10' },
  done: { label: 'Tamamlandı', color: 'text-green-400', bg: 'bg-green-500/10' }
};

// Auth utilities
export function getToken() {
  return localStorage.getItem('lifeos_token');
}

export function setToken(token) {
  localStorage.setItem('lifeos_token', token);
}

export function removeToken() {
  localStorage.removeItem('lifeos_token');
}

export function getUser() {
  const user = localStorage.getItem('lifeos_user');
  return user ? JSON.parse(user) : null;
}

export function setUser(user) {
  localStorage.setItem('lifeos_user', JSON.stringify(user));
}

export function removeUser() {
  localStorage.removeItem('lifeos_user');
}

export function isAuthenticated() {
  return !!getToken();
}

export function logout() {
  try {
    removeToken();
    removeUser();
    Object.keys(localStorage)
      .filter((k) => k.startsWith('lifeos_') || k === 'mife_intake_draft' || k.startsWith('cd_'))
      .forEach((k) => localStorage.removeItem(k));
    sessionStorage.clear();
  } catch (e) {
    console.warn('logout: storage clear partial failure', e);
  }
  window.location.assign('/');
}

// API helper with robust error handling
export async function apiRequest(endpoint, options = {}) {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers
  };

  let response;
  try {
    response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers
    });
  } catch (networkError) {
    throw new Error('Bağlantı hatası. Lütfen internet bağlantınızı kontrol edin.');
  }

  // Read response as text first, then parse as JSON
  let text = '';
  let data = null;
  
  try {
    text = await response.text();
    if (text) {
      data = JSON.parse(text);
    }
  } catch (parseError) {
    console.error('API response parse error:', response.status, text?.substring(0, 200));

    // Success response with an empty/whitespace body — treat as no content.
    if (response.ok) {
      return null;
    }
    // Preserve the HTTP status so callers can route (e.g. 403 → not-a-mentor).
    if (response.status === 401) {
      logout();
    }
    const errP = new Error(
      response.status >= 500 ? 'Sunucu hatası. Lütfen daha sonra tekrar deneyin.' : 'Beklenmeyen sunucu yanıtı'
    );
    errP.status = response.status;
    throw errP;
  }

  if (response.status === 401) {
    logout();
    throw new Error('Oturum süresi doldu');
  }

  if (!response.ok) {
    const detail = data?.detail;
    let msg;
    if (detail && typeof detail === 'object') {
      // FastAPI structured detail (e.g. paywall_required) — preserve full payload
      msg = detail.code ? `${detail.code}: ${JSON.stringify(detail)}` : JSON.stringify(detail);
    } else {
      msg = detail || data?.message || 'Bir hata oluştu';
    }
    const err = new Error(msg);
    err.status = response.status;
    err.detail = detail;
    throw err;
  }

  return data;
}


// Multipart file upload helper. Browser sets the multipart boundary, so we must
// NOT set Content-Type manually. Returns the parsed JSON (e.g. {url, path}).
export async function uploadFile(endpoint, file, fieldName = 'file') {
  const token = getToken();
  const form = new FormData();
  form.append(fieldName, file);
  let response;
  try {
    response = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      body: form,
    });
  } catch (e) {
    throw new Error('Bağlantı hatası. Lütfen tekrar dene.');
  }
  let data = null;
  try { const t = await response.text(); data = t ? JSON.parse(t) : null; } catch (e) { /* noop */ }
  if (response.status === 401) { logout(); throw new Error('Oturum süresi doldu'); }
  if (!response.ok) {
    const err = new Error(data?.detail || data?.message || 'Yükleme başarısız');
    err.status = response.status;
    throw err;
  }
  return data;
}

// Authenticated/public file download → triggers browser save. Works for PDF
// endpoints (auth header attached when a token exists; harmless for public ones).
export async function downloadFile(endpoint, filename) {
  const token = getToken();
  const res = await fetch(`${API_URL}${endpoint}`, {
    headers: { ...(token && { Authorization: `Bearer ${token}` }) },
  });
  if (!res.ok) throw new Error('İndirme başarısız oldu.');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

